// CONTRACT:C2-RENDER-ENGINE.1.0
/** Pure GEOMETRIC SANITY GUARDS for the COP render lane — the clamps + finite-checks that stop a
 *  single bad coordinate, a motion-model loop-wrap teleport, a runaway vector multiplier, or a
 *  garbage altitude from drawing a screen-spanning "laser" line / screen-tall bar.
 *
 *  WHY: deck.gl LineLayer / PathLayer happily draw a segment to a NON-FINITE endpoint (a NaN /
 *  Infinity coordinate streaks a line across the whole viewport) and to a FAR endpoint (a
 *  duration_s heading-wrap snaps a fast track ~200nm back to its start — the comet-tail / future-
 *  path then draws a crisp line across the theatre). `typeof NaN === "number"` is TRUE, so a bare
 *  type check is NOT enough — these are the finite + continuity guards the legacy pointOf lacked.
 *
 *  The deck backend (apps/cop/src/host/deckBackend.ts) feeds every line / path / stalk / leader
 *  endpoint through these so non-finite or discontinuous geometry is DROPPED or TRUNCATED, never
 *  rendered. PRESENTATION ONLY (I13): these bound EXTENT / continuity, never WHAT renders.
 *
 *  PURE: imports nothing, deterministic, no GPU. Tested directly in
 *  packages/render-engine/test/geometryGuards.test.ts. */

/** Flat-projection constant (nautical miles per degree of latitude). */
const NM_PER_DEG_LAT = 60;
const DEG2RAD = Math.PI / 180;

/** A lng/lat[/alt] tuple as the render lane carries it (GeoJSON order: [lon, lat, ...]). */
export type LngLat = ArrayLike<number>;

/** True iff BOTH lng and lat are FINITE real numbers — rejects NaN, ±Infinity, and non-numbers.
 *  `typeof NaN === "number"` is true, so the legacy `typeof c[0] === "number"` check passed NaN
 *  straight through to a LineLayer; Number.isFinite is the guard that actually closes that hole. */
export function isFiniteLngLat(p: LngLat | null | undefined): boolean {
  return p != null && Number.isFinite(p[0]) && Number.isFinite(p[1]);
}

/** Flat-earth nautical-mile distance between two lng/lat points (altitude ignored). Returns
 *  +Infinity when either endpoint is non-finite, so a teleport/continuity check treats garbage as
 *  an immediate "break" (it will always exceed any finite cap). */
export function segmentNm(a: LngLat, b: LngLat): number {
  if (!isFiniteLngLat(a) || !isFiniteLngLat(b)) return Infinity;
  const dLat = (b[1] - a[1]) * NM_PER_DEG_LAT;
  const dLon = (b[0] - a[0]) * NM_PER_DEG_LAT * Math.cos(((a[1] + b[1]) / 2) * DEG2RAD);
  return Math.hypot(dLat, dLon);
}

/** Max plausible distance (nm) between two CONSECUTIVE render samples — a comet-tail tick-to-tick
 *  step or a future-path +30s step — before the jump is treated as a TELEPORT / discontinuity (a
 *  motion-model loop-wrap, e.g. the heading-model duration_s wrap that snaps a track ~210nm back
 *  to its start) and the line is BROKEN there. A hypersonic ~4000kn track covers only ~33nm in a
 *  30s step, so 60nm cleanly separates a legitimate fast step from a wrap teleport. */
export const MAX_RENDER_SEG_NM = 60;

/** Absolute ceiling (nm) on a velocity-leader length AFTER the user vectorScale multiplier, so a
 *  large vectorScale (up to 8×) can never stretch the leader into a screen-spanning streak. The
 *  legacy resting cap is ~36nm (scale 1); this is the hard outer bound past which scale can't go. */
export const LEADER_MAX_NM = 120;

/** The clamped velocity-leader LENGTH (nm) for a track: legacy base = speed·(2/60) clamped to
 *  [1.5, 36] nm (~2 min ahead) × a positive vectorScale, then hard-capped at {@link LEADER_MAX_NM}.
 *  Non-finite speed → 0 (no leader). At scale 1 this is the legacy length EXACTLY (max 36nm, well
 *  under the cap); only a large scale ever trips the cap. deckBackend.leaderTarget projects this
 *  length along the heading. */
export function leaderLengthNm(speedKn: number, scale = 1): number {
  if (!Number.isFinite(speedKn)) return 0;
  const s = Number.isFinite(scale) && scale > 0 ? scale : 1;
  const base = Math.min(Math.max(speedKn * (2 / 60), 1.5), 36) * s;
  return Math.min(Math.max(base, 0), LEADER_MAX_NM);
}

/** Sane altitude ceiling (metres HAE) — comfortably above any aircraft or hypersonic-glide near-
 *  space band (~27km), so every legitimate altitude is untouched, but a garbage / out-of-range
 *  altitude is clamped and an air drop-line stalk can never shoot a screen-tall bar off the top of
 *  the scope. */
export const MAX_ALT_M = 60_000;

/** Clamp an altitude (m HAE) to [0, {@link MAX_ALT_M}]; non-finite → 0. The render lane's zOf runs
 *  every altitude through this so no layer (marker, halo, smear, stalk) ever lifts to a garbage Z. */
export function clampAltM(m: unknown): number {
  return typeof m === "number" && Number.isFinite(m) ? Math.max(0, Math.min(MAX_ALT_M, m)) : 0;
}

/** The leading CONTINUOUS, FINITE run of a polyline: starting at the first point, keep appending
 *  while each point is finite AND within `maxSegNm` of the previous one. Stops at the first non-
 *  finite point or teleport (> maxSegNm) — so a future-path that crosses a duration_s wrap is CUT
 *  at the wrap instead of drawing a line across the theatre. Returns [] when the first point is
 *  non-finite; a length-1 result means "no drawable segment" (the caller skips it). PURE. */
export function continuousRun<T extends LngLat>(
  points: readonly T[],
  maxSegNm = MAX_RENDER_SEG_NM,
): T[] {
  const out: T[] = [];
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    if (!isFiniteLngLat(p)) break;
    if (i > 0 && segmentNm(out[out.length - 1], p) > maxSegNm) break;
    out.push(p);
  }
  return out;
}
